(ns dashboard.entity.analysis.search
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.components.popup-menu :as popup-menu]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.entity.analysis.tree :as analysis.tree]
            [dashboard.services.http-config :as http-config]
            [dashboard.utils :as utils]
            [re-frame.core :as rf]
            clojure.set))

;; ----------------------------------------------------------------------------
;;  Similarity Search:
;; The backend API will respond to  query with BOTH recommnded questions and
;; analysis, hydrating both portions of the recommendation engine.
;; The query results are cached together and have a 30min ttl that we may want
;; to adjust later.
;;
;; This File contains 4 main sections:
;; 1. Parameterized re-frame app DB calls that should NOT be accessed directly.
;; 2. Getter and setter functions
;; 3. The search input control, common to both question search and analysis
;;    search.
;; 4. The Analysis Search Component
;; ----------------------------------------------------------------------------

;; ----------------------------------------------------------------------------
;; Parameterized re-frame app DB calls that should NOT be accessed directly.
;; ----------------------------------------------------------------------------
(rf/reg-event-db
 ::set-search-success
 (fn [db [_ path query context results]]
   (let [results (->> results
                      :structures
                      (group-by (comp first :categories))
                      (mapv (fn [[_ coll]]
                              (->> coll
                                   (take 3)
                                   (filter #(> (:similarity %) 0.15))
                                   vec)))
                      flatten
                      (mapv :construct-id)
                      set
                      (assoc results :analysis-ids))]
     (-> db
         (assoc :loading-recommendations false)
         (assoc-in (conj path :data) results)
         (assoc-in (conj path :context) context)
         (assoc-in [:analysis-search-cache :cache (hash query)] {:data results
                                                                 :time (js/Date.now)})))))

(rf/reg-sub
 ::get-search-results
 (fn [db [_ path]]
   (get-in db (conj path :data))))

(rf/reg-event-db
 ::set-exact-search-results
 (fn [db [_ path results]]
   (-> db
       (assoc-in (conj path :data) {:analysis-ids results}))))

(rf/reg-event-db
 ::set-search-failure
 (fn [db [_ & args]]
   (assoc db :loading-recommendations false)))

(defn more-than-30-minutes-ago? [js-date]
  (let [now (js/Date.now)
        thirty-minutes (* 30 60 1000)
        time-difference (- now js-date)]
    (> time-difference thirty-minutes)))

(rf/reg-event-fx
 ::search-for-similar-analyses
 (fn [{:keys [db]} [_ path query context date-range]]
   (let [hash-code (hash (str query " - " date-range))]
     (if (and (get-in db [:analysis-search-cache :cache hash-code :data])
              (get-in db [:analysis-search-cache :cache hash-code :time]))
       {:db (-> db
                (assoc-in (conj path :data) (get-in db [:analysis-search-cache :cache hash-code :data]))
                (assoc-in (conj path :context) context)
                (assoc-in (conj path :query-id) (gensym :analysis.query))
                (cond->
                 (and (get-in db [:analysis-search-cache :cache hash-code :time])
                      (more-than-30-minutes-ago? (get-in db [:analysis-search-cache :cache hash-code :time])))
                  (assoc-in [:analysis-search-cache :cache hash-code] nil)))}
       {:db (assoc-in db [:loading-recommendations] true)
        :http-xhrio {:method          :post
                     :uri             (if (-> db
                                              :user
                                              :features_enabled
                                              set
                                              (contains? :structured-analysis-creation)
                                              not)
                                        "/api/recommendations/search/"
                                        "/api/recommendations/structure-search/")
                     :params          {:query query :date-range date-range}
                     :parameters      {:query query :date-range date-range}
                     :timeout         http-config/timeout
                     :format          http-config/transit-request-format
                     :response-format http-config/transit-response-format
                     :on-success      [::set-search-success path query context]
                     :on-failure      [::set-search-failure "Failed to retrieve results"]}}))))

(rf/reg-sub
 ::loading-recommendations
 (fn [db _]
   (get db :loading-recommendations)))

(defn loading-recommendations*
  []
  (rf/subscribe [::loading-recommendations]))

(rf/reg-sub
 ::get-query-context
 (fn [db [_ path initial-value]]
   (get-in db (conj path :context) initial-value)))

(rf/reg-event-db
 ::set-query-context
 (fn [db [_ path context-text]]
   (-> db
       (assoc-in (conj path :context) context-text))))

(rf/reg-sub
 ::get-query
 (fn [db [_ path initial-value]]
   (get-in db (conj path :query) initial-value)))

(rf/reg-sub
 ::get-query-id
 (fn [db [_ path]]
   (get-in db (conj path :query-id))))

(rf/reg-event-db
 ::set-query
 (fn [db [_ path query]]
   (-> db
       (assoc-in (conj path :query) query))))

(rf/reg-sub
 ::get-external-query-update
 (fn [db [_ path]]
   (get-in db (conj path :external-query))))

(rf/reg-event-db
 ::set-external-query-update
 (fn [db [_ path]]
   (assoc-in db (conj path :external-query) (random-uuid))))

(rf/reg-sub
 ::get-selected-question
 (fn [db [_ path]]
   (get-in db (conj path :selected-question))))

(rf/reg-sub
 ::get-selected-analysis
 (fn [db [_ path]]
   (get-in db (conj path :selected-analysis))))

(rf/reg-event-db
 ::set-selected-question
 (fn [db [_ path question]]
   (assoc-in db (conj path :selected-question) question)))

(rf/reg-event-db
 ::set-selected-analysis
 (fn [db [_ path analysis]]
   (assoc-in db (conj path :selected-analysis) analysis)))

;; ----------------------------------------------------------------------------
;; Getter and setter functions
;; ----------------------------------------------------------------------------

(defn exact-search-results
  [query-string analyses]
  (->> analyses
       (filter
        (fn [structured-analysis]
          (analysis.profile/search-query-tree-filter
           (or query-string "")
           structured-analysis)))
       (mapv :construct-id)
       set))

(defn get-search-query-context*
  [path initial-value]
  (rf/subscribe [::get-query-context path initial-value]))

(defn set-search-query-context
  [path context-text & [internal-update]]
  (when-not (true? internal-update)
    (rf/dispatch [::set-external-query-update path]))
  (rf/dispatch [::set-query path context-text]))

(defn get-search-query-id*
  [path]
  (rf/subscribe [::get-query-id path]))

(defn get-search-query*
  [path initial-value]
  (rf/subscribe [::get-query path initial-value]))

(defn set-search-query
  [path query & [internal-update]]
  (when-not (true? internal-update)
    (rf/dispatch [::set-external-query-update path]))
  (rf/dispatch [::set-query path query]))

(defn perform-search
  [{:keys [mode path analyses set-query context date-range]
    :or {date-range 3}}
   query]
  (if (= mode :semantic)
    (rf/dispatch [::search-for-similar-analyses
                  path
                  (cond->> query
                    context
                    (str context " : "))
                  context
                  date-range])
    (rf/dispatch [::set-exact-search-results
                  path
                  (exact-search-results
                   query
                   analyses)]))
  ((or set-query identity) query))

(def debounced-search
  (utils/debounce
   perform-search
   400))

(defn all-analysis-structures
  []
  (analysis.db/analysis-list*))

(defn get-search-results*
  [path]
  (rf/subscribe [::get-search-results path]))

(defn set-selected-question
  [path question]
  (rf/dispatch [::set-selected-question path question]))

(defn get-selected-question*
  [path]
  (rf/subscribe [::get-selected-question path]))

(defn set-selected-analysis
  [path analysis]
  (rf/dispatch [::set-selected-analysis path analysis]))

(defn get-selected-analysis*
  [path]
  (rf/subscribe [::get-selected-analysis path]))

;; ----------------------------------------------------------------------------
;; The search input control, common to both question search and analysis
;; search.
;; ----------------------------------------------------------------------------

(defn SearchInput
  [{:keys [query-mode
           set-query-mode
           set-query
           search-actions
           props
           search-context
           wrapper-class]}]
  [:div.analysis-search-input
   {:class [(if (= query-mode :exact)
              :exact-search
              :semantic-search)
            wrapper-class]}
   [:div.analysis-search-input--title
    [:div.title.is-7.my-0
     [:i.icon.fas.fa-search]
     (if (= search-context :question) "Question" "Analysis")
     " Search: "
     (-> query-mode name str/capitalize)
     " Matching"]]
   [:input.input.mb-0.mt-1
    (merge props
           {:type :search
            :placeholder (if (= query-mode :exact)
                           "Type your query to filter Analyses by words and fragments. Use double-quotes to match phrases."
                           "Type your query to filter Analyses by semantic matching. Either phrases or keywords can be used")})]

   [:div.analysis-search-input--button-group
    [:div.analysis-search-input--buttons
     [:span.is-size-7.mr-2 "Query Mode: "]
     [:div
      (when-not (= search-context :question)
        [:button.button.my-0.search-control--buttons
         {:class (if (= query-mode :exact) :is-primary :is-secondary)
          :title "Use Exact (legacy) matching to search across analyses"
          :on-click #(set-query-mode :exact)}
         "Exact"])
      [:button.button.my-0.search-control--buttons
       {:class (if (= query-mode :semantic) :is-info :is-secondary)
        :title "Use Semantic matching to search across analyses' titles for similar and relevant phrases"
        :on-click #(set-query-mode :semantic)}
       "Semantic"]]]
    [:div.analysis-search-input--buttons
     [:div.buttons.my-0
      [:button.button.my-0.is-danger.is-outlined.search-control--buttons
       {:title "Clear Search"
        :on-click #(set-query "")}
       [:i.icon.fas.fa-delete-left]]
      search-actions]]]])

;; ----------------------------------------------------------------------------
;; Filters For Question Search
;; ----------------------------------------------------------------------------
(defn QuestionFilterOptions
  [{:keys [use-hierarchy-context
           set-use-hierarchy-context
           perform-search
           question]}]
  [:<>
   [:button.button.is-secondary.my-0.is-outlined.search-control--buttons
    {:title "Reset Search to Question Text"
     :on-click (fn [_] (perform-search (:content question)))}
    [:i.icon.fas.fa-refresh]]

   [:input.ml-3.mr-1
    {:type "checkbox"
     :checked use-hierarchy-context
     :disabled (empty? (:tree/ancestor-nodes question))
     :on-change #(set-use-hierarchy-context not)}]

   [:f> popup-menu/popup-menu
    {:suspend-click-to-close true
     :disabled (empty? (:tree/ancestor-nodes question))
     :trigger-class [:is-outlined :is-secondary :my-0 :search-control--buttons]
     :menu-class :search-include-hierarchy-context-info
     :trigger-content [:<>
                       "Hierarchy"
                       [:i.icon.fas.fa-bullseye.ml-2]
                       [:i.icon.fas.fa-info-circle.ml-2]]
     :content [:div
               (some->> (:tree/ancestor-nodes question)
                        (mapv :content)
                        (map-indexed (fn [i c] [:div.level-left.is-align-items-start
                                                {:style {:margin-left (str (* 52 i) "px")}}
                                                [:div "> "]
                                                [:div.ml-2 c]]))
                        (into [:<>]))]}]])


;; ----------------------------------------------------------------------------
;; Analysis Search Component
;; ----------------------------------------------------------------------------

(defn StateFilter
  [filter-states set-filter-states all-states]
  (let [dummy (react/useState nil)]
    (->> all-states
         (mapv
          (fn [state]
            [:div.analysis-search--state-filter-menu-option
             [:input.mr-2
              {:type "checkbox"
               :checked (contains? filter-states state)
               :on-change #(set-filter-states
                            ((if (contains? filter-states state)
                               disj
                               conj)
                             filter-states
                             state))}]
             state]))
         (into [:div {:key :statis}]))))

(defn AnalysisSearch
  [{:keys [analyses
           category
           filter-actions
           initial-search
           question
           hide-input
           on-click-construct
           on-click-state
           on-select
           hide-initial-search
           path
           preselected-version-ids
           disabled-construct-ids
           row-component
           search-actions
           selected-version-ids
           use-hierarchy-context
           set-use-hierarchy-context
           show-invalid-filter]
    :as props}]
  (let [;ukee
        all-states ["True"
                    "False"
                    "Not Yet Evaluated"
                    "Out of scope"
                    "Needs more information"]
        hierarchy-context (->> (mapv :content (:tree/ancestor-nodes question))
                               (interpose " : ")
                               (apply str))
        ; subs
        query @(get-search-query* path (:query initial-search))
        external-query-update @(rf/subscribe [::get-external-query-update path])
        analyses (or analyses @(all-analysis-structures))
        search-results-data @(get-search-results* path)
        loading-recommendations @(loading-recommendations*)

        ; state
        [display-query  set-display-query] (react/useState query)
        [show-search set-show-search] (react/useState (if (boolean? hide-initial-search)
                                                        (not hide-initial-search)
                                                        true))
        [filter-states set-filter-states] (react/useState (set all-states))
        [query-mode set-query-mode] (react/useState (or (:mode initial-search)
                                                        :exact))
        [search-result-construct-ids
         set-search-result-construct-ids] (react/useState
                                           (or (->> props
                                                    :analyses
                                                    (exact-search-results query))
                                               (mapv :construct-id analyses)))
        [filter-has-invalid set-filter-has-invalid] (react/useState false)
        ; re-attach analyses to props
        props (assoc props :analyses analyses)
        ; handlers
        on-query #(set-search-query path % true)
        on-search (partial debounced-search
                           {:mode (or query-mode :exact)
                            :path path
                            :analyses analyses
                            :context (when (and question use-hierarchy-context)
                                       (->> (mapv :content (:tree/ancestor-nodes question))
                                            (interpose " : ")
                                            (apply str)))
                            :set-query on-query})
        on-update-query (fn [query]
                          (set-display-query query)
                          (on-search query))]
    (react/useEffect
     (fn [_]
       (->> search-results-data
            :analysis-ids
            set
            set-search-result-construct-ids))
     #js [search-results-data])

    (react/useEffect
     (fn [_] (set-display-query query))
     #js [external-query-update])

    (react/useEffect
     (fn [_]
       (when-not (str/blank? query)
         (perform-search {:mode (or query-mode :exact)
                          :path path
                          :analyses analyses
                          :context (when (and question use-hierarchy-context)
                                     hierarchy-context)
                          :set-query on-query}
                         query)))
     #js [query-mode use-hierarchy-context question])

    [:div.analysis-search
     (when-not hide-input
       (if show-search
         [SearchInput
          {:query-mode query-mode
           :set-query-mode set-query-mode
           :set-query on-update-query
           :search-actions [:div.analysis-search--options-filter-menu
                            [:f> popup-menu/popup-menu
                             {:suspend-click-to-close true
                              :trigger-class [:is-outlined
                                              :search-control--buttons
                                              :is-secondary
                                              :my-0
                                              :mr-2]
                              :trigger-content (cond
                                                 (= filter-states (set all-states))
                                                 "All States"
                                                 :else
                                                 (let [other (apply disj filter-states #{"True" "False"})
                                                       other-count (count other)
                                                       true-or-false (or (filter-states "True")
                                                                         (filter-states "False"))]
                                                   (str
                                                    (when (filter-states "True") "True")
                                                    (when (and (filter-states "True")
                                                               (filter-states "False"))
                                                      ", ")
                                                    (when (filter-states "False") "False")
                                                    (when (and true-or-false (pos? other-count))
                                                      " + ")
                                                    (if (pos? other-count)
                                                      (if (and (not true-or-false)
                                                               (= 1 other-count))
                                                        (first other)
                                                        (str other-count " States"))
                                                      (when (zero? (count filter-states))
                                                        "All States Hidden!!!")))))
                              :menu-class :analysis-search--state-filter-menu
                              :on-close identity
                              :disabled false
                              :content [:f> StateFilter
                                        filter-states
                                        set-filter-states
                                        all-states]}]
                            (when show-invalid-filter
                              [:<>
                               [:input.mr-2
                                {:type :checkbox
                                 :checked filter-has-invalid
                                 :on-change #(set-filter-has-invalid not)}]
                               "Has Invalid Evidence"])

                            search-actions]
           :use-hierarchy-context use-hierarchy-context
           :set-use-hierarchy-context set-use-hierarchy-context
           :props {:value display-query
                   :on-change (fn [e] (-> e .-target .-value  on-update-query))}
           :wrapper-class ""}]
         [:div.m-0
          [:button.button.is-info.my-1.is-outlined
           {:on-click #(set-show-search true)}
           [:i.icon.fas.fa-search.mr-3]
           "Refine Search"]]))

     filter-actions

     (let [filtered-analyses (->> analyses
                                  (filter
                                   (fn [asa]
                                     (->> asa
                                          :applied-analyses
                                          first
                                          :state-id
                                          (get (into {} (:states asa)))
                                          str
                                          str/lower-case
                                          (contains? (->> filter-states
                                                          (mapv str/lower-case)
                                                          set))))))
           filtered-construct-ids (->> filtered-analyses
                                       (mapv :construct-id)
                                       set)
           visibile-construct-ids (analysis.tree/get-analysis-tree-displayed-construct-ids
                                   analyses
                                   filtered-construct-ids)
           invalid-construct-ids (if filter-has-invalid
                                   (->> filtered-analyses
                                        (keep
                                         (fn [{:keys [applied-analyses]}]
                                           (let [{:keys [evidence-count
                                                         controls-met
                                                         construct-id]} (first applied-analyses)]
                                             (when (and (pos? evidence-count)
                                                        (> evidence-count controls-met))
                                               construct-id))))
                                        vec)
                                   [])
           search-result (if-not (str/blank? query)
                           search-result-construct-ids
                           (if (seq search-result-construct-ids)
                             search-result-construct-ids
                             (mapv :construct-id analyses)))]

       [:f> analysis.tree/AnalysisTree
        (assoc props
               :category (or category "Operations (process)")
               :loading loading-recommendations
               :analyses (filterv
                          (comp (partial contains? visibile-construct-ids) :construct-id)
                          analyses)
               :disabled-construct-ids (apply conj
                                              (apply disj visibile-construct-ids filtered-construct-ids)
                                              disabled-construct-ids)
               :search-result-construct-ids (if filter-has-invalid
                                              (clojure.set/intersection
                                               (set search-result)
                                               (set invalid-construct-ids))
                                              search-result)
               :display-construct-ids (if (seq invalid-construct-ids)
                                        invalid-construct-ids
                                        (apply conj
                                               filtered-construct-ids
                                               visibile-construct-ids))
               :active-query (or (not (str/blank? query))
                                 filter-has-invalid)
               :row-component row-component)])]))